import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import {
  AlarmClockOff,
  Mail,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";

function formatDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function fullName(u: { firstName?: string | null; lastName?: string | null; email?: string | null }): string {
  const n = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
  return n || u.email || "—";
}

function daysSince(d: string | Date | null | undefined): string {
  if (!d) return "jamais";
  const ms = Date.now() - new Date(d).getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  return `${days}j`;
}

export default function OpsInactiveArtisans() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);

  const [daysInput, setDaysInput] = useState<number>(30);
  const [appliedDays, setAppliedDays] = useState<number>(30);
  const [page, setPage] = useState(1);

  const utils = trpc.useUtils();
  const { data, isLoading, error } = trpc.admin.opsAntiFraud.listInactiveArtisans.useQuery({
    daysSinceActivity: appliedDays,
    page,
    limit: 25,
  });

  const [reactivateTarget, setReactivateTarget] = useState<{ id: string; label: string } | null>(null);
  const [customMessage, setCustomMessage] = useState("");

  const reactivateMutation = trpc.admin.opsAntiFraud.triggerReactivationEmail.useMutation({
    onSuccess: () => {
      toast.success("Email de relance envoye");
      setReactivateTarget(null);
      setCustomMessage("");
      utils.admin.opsAntiFraud.listInactiveArtisans.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erreur lors de l'envoi"),
  });

  const handleApplyFilter = () => {
    if (daysInput < 1 || daysInput > 365) {
      toast.error("Plage valide : 1 a 365 jours");
      return;
    }
    setAppliedDays(daysInput);
    setPage(1);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Relance artisans inactifs</h1>
            <p className="text-muted-foreground text-sm">
              Artisans verifies sans activite recente — declenchez une relance email pour les recuperer.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => utils.admin.opsAntiFraud.listInactiveArtisans.invalidate()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Rafraichir
          </Button>
        </div>

        {/* Filter */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Inactifs depuis X jours
                </label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={daysInput}
                  onChange={(e) => setDaysInput(Number(e.target.value) || 0)}
                  onKeyDown={(e) => e.key === "Enter" && handleApplyFilter()}
                  className="h-9 w-32"
                />
              </div>
              <Button size="sm" onClick={handleApplyFilter}>
                <Filter className="h-4 w-4 mr-2" />
                Filtrer
              </Button>
              {data && (
                <span className="text-xs text-muted-foreground ml-auto">
                  Cutoff : avant {formatDate(data.cutoff)} — {data.total} artisan{data.total !== 1 ? "s" : ""} concerne{data.total !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Artisan</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Documents</TableHead>
                  <TableHead>Credits leads</TableHead>
                  <TableHead>Derniere activite</TableHead>
                  {canAct && <TableHead className="text-right">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={canAct ? 6 : 5} className="text-center py-8 text-muted-foreground">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={canAct ? 6 : 5} className="text-center py-8 text-destructive">
                      {error.message}
                    </TableCell>
                  </TableRow>
                ) : !data?.items.length ? (
                  <TableRow>
                    <TableCell colSpan={canAct ? 6 : 5} className="text-center py-8 text-muted-foreground">
                      <AlarmClockOff className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      Aucun artisan inactif sur cette periode.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.items.map((a) => {
                    const u = a.User;
                    return (
                      <TableRow key={a.id}>
                        <TableCell>
                          <div>
                            <span className="font-medium">{a.companyName}</span>
                            <span className="block text-xs text-muted-foreground">{fullName(u)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">{u.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {a._count.ArtisanDocuments} doc{a._count.ArtisanDocuments !== 1 ? "s" : ""}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {u.Company ? (
                            <Badge
                              variant="outline"
                              className={
                                u.Company.leadCredits === 0
                                  ? "border-destructive text-destructive"
                                  : u.Company.leadCredits < 3
                                  ? "border-orange-500 text-orange-600"
                                  : "border-emerald-500 text-emerald-600"
                              }
                            >
                              {u.Company.leadCredits}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {a.lastActivityAt ? (
                            <div>
                              <span className="text-sm">{formatDate(a.lastActivityAt)}</span>
                              <span className="block text-xs text-muted-foreground">
                                il y a {daysSince(a.lastActivityAt)}
                              </span>
                            </div>
                          ) : (
                            <Badge variant="outline" className="border-destructive/50 text-destructive text-xs">
                              jamais
                            </Badge>
                          )}
                        </TableCell>
                        {canAct && (
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7"
                              onClick={() =>
                                setReactivateTarget({
                                  id: a.id,
                                  label: `${a.companyName} (${u.email})`,
                                })
                              }
                            >
                              <Mail className="h-3.5 w-3.5 mr-1" />
                              Relancer
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {data && data.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <span className="text-sm text-muted-foreground">
                  Page {data.page} / {data.totalPages} — {data.total} artisans
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="h-8"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= (data.totalPages || 1)}
                    onClick={() => setPage((p) => p + 1)}
                    className="h-8"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reactivation dialog */}
      <Dialog
        open={!!reactivateTarget}
        onOpenChange={(o) => {
          if (!o) {
            setReactivateTarget(null);
            setCustomMessage("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Relancer cet artisan ?</DialogTitle>
            <DialogDescription>
              {reactivateTarget?.label}
              <br />
              Un email de reactivation sera envoye et lastActivityAt sera mis a jour pour eviter les relances en boucle.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">
              Message personnalise (optionnel)
            </label>
            <Textarea
              placeholder="Ex : Nous avons remarque que vous n'avez pas consulte les nouveaux projets dans votre zone…"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={4}
              maxLength={2000}
            />
            <p className="text-xs text-muted-foreground text-right">{customMessage.length}/2000</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReactivateTarget(null)}>
              Annuler
            </Button>
            <Button
              onClick={() => {
                if (!reactivateTarget) return;
                reactivateMutation.mutate({
                  artisanProfileId: reactivateTarget.id,
                  customMessage: customMessage.trim() || undefined,
                });
              }}
              disabled={reactivateMutation.isPending}
            >
              {reactivateMutation.isPending ? "Envoi..." : "Envoyer la relance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
